import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics/types/enums';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import type { PromptContentChangePayload } from '@atlassian/conversation-assistant-chat-prompt-input/prompt-content-change-payload';
import { RovoInlineChatPrompt } from '@atlassian/conversation-assistant-chat-prompt-input/ui/inline-chat-prompt';
import {
	ExperienceName,
	remixEditExistingExperience,
	REMIX_EDIT_EXISTING_MARKS,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { Breakpoint } from '@atlassian/generative-ai-modal/hooks/useElementBreakpoints';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { ROVO_EXPERIENCE_TRACKING_SENDER_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-experience-tracking-sender-client';
import { AifcExperienceTrackingCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/aifc-preview-experience-tracking-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import type {
	InlineRovoEditAdmissionResult,
	InlineRovoEditProducerAttemptObservation,
} from '../../../../editor-actions/types';
import { resetSessionState } from '../../../../pm-plugins/ai-session-context/commands';
import {
	createCommitInlineRovoStreamingCommand,
	createEndInlineRovoStreamingCommand,
	createShowInlineEditReadyCommand,
} from '../../../../pm-plugins/decoration/actions';
import { aiExperienceDecorationPluginKey } from '../../../../pm-plugins/decoration/decoration-plugin-key';
import type { EditorPluginAIProvider } from '../../../../types/types';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { CONFIG_ITEM_KEYS } from '../../../prebuilt/config-items/config-item-keys';
import type { UserFlowAEP } from '../../../utils/analytics/analyticsFlowTypes';
import {
	isCollapseLoadingBarEnabled,
	isCollapseLoadingBarEnabledNoExposure,
} from '../../../utils/collapse-loading-bar-enabled';
import { getLoadingBarScrollContainer } from '../../../utils/get-loading-bar-scroll-container';
import { isPromptUpdatesEnabledNoExposure } from '../../../utils/prompt-updates-enabled';
import { Container } from '../../SessionUI/Container';
import { messages } from './messages';

export const providerProductToProductKey = (product: EditorPluginAIProvider['product']): string => {
	switch (product) {
		case 'CONFLUENCE':
			return 'confluence';
		case 'JSM':
			return 'jsm';
		case 'JPD':
			return 'jpd';
		case 'JSW':
		case 'JWM':
			return 'jira';
		case 'ATLAS':
		case 'TOWNSQUARE':
			return 'atlas';
		case 'BITBUCKET':
			return 'bitbucket';
		default:
			return 'unknown';
	}
};

const backupChannelVariables = {
	cloudId: '',
	orgId: undefined,
	userId: undefined,
};

const isMauiAppNode = (node?: JSONNode) =>
	node?.type === 'extension' &&
	node?.attrs != null &&
	'extensionKey' in node.attrs &&
	node.attrs.extensionKey === 'native-embed:maui';

const isMediaNode = (node?: JSONNode) => {
	if (!node) {
		return false;
	}

	if (node.type === 'media') {
		return true;
	}

	if (node.type === 'mediaSingle' && Array.isArray(node.content) && node.content.length > 0) {
		return (node.content[0] as JSONNode | undefined)?.type === 'media';
	}

	return false;
};

const getSelectionLabsLozengeVariant = (
	selectionFragment: JSONNode[] | null,
): 'default' | 'maui' | undefined => {
	if (!Array.isArray(selectionFragment) || selectionFragment.length === 0) {
		return undefined;
	}

	if (
		selectionFragment.some((node) => isMauiAppNode(node)) ||
		selectionFragment.some((node) => isMediaNode(node))
	) {
		return 'maui';
	}

	return undefined;
};

const PLUGIN_COMPONENTS_SELECTOR = '[data-testid="plugins-components-wrapper"]';

/** Place Thinking in the same loading container used by the Editing bar. */
const ThinkingBarDock = ({ children }: { children: React.ReactNode }) => {
	const { appearance } = useContentComponentContext();

	// `undefined` means the anchor has not measured yet, `null` means no plugin
	// component layer was found and the bar renders in place.
	const [portalTarget, setPortalTarget] = useState<HTMLElement | null | undefined>(undefined);

	const measurePortalTarget = useCallback((anchor: HTMLDivElement | null) => {
		if (anchor) {
			setPortalTarget(anchor.closest<HTMLElement>(PLUGIN_COMPONENTS_SELECTOR));
		}
	}, []);

	if (portalTarget === undefined) {
		return <div ref={measurePortalTarget} />;
	}

	const dock = <Container appearance={appearance}>{children}</Container>;

	return portalTarget ? createPortal(dock, portalTarget) : dock;
};

interface RovoPromptScreenWithLogicProps {
	cancel: (options?: { shouldClearSelectionContext?: boolean }) => void;
	/**
	 * When provided, intercepts the prompt submit to open the Rovo chat sidebar
	 * instead of performing an AI inline edit. The callback receives the prompt
	 * DocNode and should return `true` to indicate it handled the submit.
	 */
	openInRovoChatConfig?: {
		onBeforeSubmit: (prompt: DocNode) => boolean;
	};
	preserveLoadingBarMinimizedOnComplete?: boolean;
}

// eslint-disable-next-line jsdoc/require-jsdoc
export function RovoPromptScreenWithLogic({
	cancel,
	openInRovoChatConfig,
	preserveLoadingBarMinimizedOnComplete = false,
}: RovoPromptScreenWithLogicProps): React.JSX.Element | null {
	const { formatMessage } = useIntl();
	const {
		setIsRovoLoading,
		setIsLoadingBarMinimized,
		api,
		targetId,
		userADFInput,
		editorPluginAIProvider,
		appearance,
		setCancelRovoPrompt,
		popupsMountPoint,
		editorView,
	} = useContentComponentContext();

	const isPromptUpdatesEnabled = isPromptUpdatesEnabledNoExposure();

	const aifcPlaceholderText = isPromptUpdatesEnabled
		? formatMessage(messages.aifcPromptPlaceholder)
		: undefined;

	const isLoadingBarCollapseEnabled = isCollapseLoadingBarEnabledNoExposure();
	const useEditingWidthThinkingBar = isLoadingBarCollapseEnabled;
	const thinkingBarScrollContainer = useMemo(
		() =>
			!editorView
				? undefined
				: getLoadingBarScrollContainer(appearance, editorView.dom, isLoadingBarCollapseEnabled),
		[appearance, editorView, isLoadingBarCollapseEnabled],
	);
	const thinkingBarWrapper = useMemo(() => {
		if (!isLoadingBarCollapseEnabled) {
			return undefined;
		}

		return (thinkingBar: React.JSX.Element) => <ThinkingBarDock>{thinkingBar}</ThinkingBarDock>;
	}, [isLoadingBarCollapseEnabled]);

	// Agent selector placement (CCI-17299). Same gate-agnostic split as the
	// placeholder above: the editor owns the rollout decision and ai-mate just
	// honours the prop, so non-AIFC consumers keep the picker inside the `+`
	// popup.
	const agentSelectorLayout = isPromptUpdatesEnabled ? 'inline' : undefined;

	const hideAgentName = isPromptUpdatesEnabled;

	const { cloudId, orgId, userId } = useMemo(
		() => editorPluginAIProvider?.getChannelVariables?.('rovo-agent') || backupChannelVariables,
		[editorPluginAIProvider],
	);
	const product = editorPluginAIProvider.product;
	const productKey = providerProductToProductKey(product);
	const isPageEditor =
		appearance === 'full-page' || appearance === 'full-width' || appearance === 'max';

	const { width } = useSharedPluginStateWithSelector(api, ['width'], (states) => {
		return {
			width: states.widthState?.width,
		};
	});

	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);
	const pendingInlineRovoStreamingTargetId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) =>
			aiExperienceState?.aiDecorationExperiencePluginState.pendingInlineRovoStreamingTargetId,
	);
	// Keep the admitted token stable so late callbacks cannot settle a replacement attempt.
	const inlineRovoEditAttemptIdRef = useRef<string | undefined>(undefined);
	const getInlineRovoEditAttemptId = useCallback(() => inlineRovoEditAttemptIdRef.current, []);
	const startInlineRovoEditHandoffPresentation = useCallback(() => {
		const attemptId = inlineRovoEditAttemptIdRef.current;
		if (attemptId) {
			api?.aiExperience?.actions.startInlineRovoEditHandoffPresentation?.(attemptId);
		}
	}, [api]);
	// Suppress prompt-close abort after an explicit Rovo Chat handoff.
	const admittedInlineRovoEditHandedOffRef = useRef(false);
	// Distinguish synchronous Chat auto-close from later user cancellation.
	const admittedInlineRovoEditPendingAutoCloseRef = useRef(false);
	const submitAutoCloseRef = useRef(false);
	const handleSubmitAutoClose = useCallback(() => {
		submitAutoCloseRef.current = true;
		startInlineRovoEditHandoffPresentation();
	}, [startInlineRovoEditHandoffPresentation]);

	const existingShowLabsLozenge = useMemo(() => {
		if (productKey === 'confluence') {
			if (!fg('aifc_onboarding_ga_enabled')) {
				return true;
			}

			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			if (!FeatureGates.getExperimentValue('cc-aifc-databases-experiment', 'isEnabled', false)) {
				return true;
			}
		}

		return false;
	}, [productKey]);

	const selectionContext = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.selectionContext ?? null,
	);

	const selectionLabsLozengeVariant = useMemo(
		() => getSelectionLabsLozengeVariant(selectionContext?.selectionFragment ?? null),
		[selectionContext],
	);

	const isMauiOrMediaSelection =
		expValEquals('cc-maui-experiment', 'isEnabled', true) &&
		selectionLabsLozengeVariant !== undefined;

	const source = isMauiOrMediaSelection
		? 'CONFLUENCE_MAUI_INLINE_EDITOR'
		: productKey === 'confluence' && isPageEditor
			? 'CONFLUENCE_INLINE_EDITOR'
			: 'INLINE_EDITOR';
	const isMauiInlineRemixEditPrompt = isMauiOrMediaSelection && !openInRovoChatConfig;
	// Keep ai-mate mounted for completion cleanup, but hide the edit prompt while
	// InlineEditReadyBar owns the post-stream UI.
	const shouldHidePromptAfterComplete =
		isMauiInlineRemixEditPrompt && isExperimentEnabled('cc_maui_polish_changes_batch_6');
	const shouldOpenChatOnError =
		(source === 'CONFLUENCE_INLINE_EDITOR' || source === 'CONFLUENCE_MAUI_INLINE_EDITOR') &&
		fg('cc-maui-phase-2') &&
		expValEquals('cc-maui-experiment', 'isEnabled', true);

	const showLabsLozenge =
		(existingShowLabsLozenge && selectionLabsLozengeVariant === undefined) ||
		(isMauiOrMediaSelection && (product !== 'CONFLUENCE' || !fg('cc_maui_ga_labs_removal')));

	// Deregisters the turn abort exposed through `aiExperience.actions.cancelInlineRovoEdit`.
	const unregisterInlineRovoEditCancellationRef = useRef<(() => void) | undefined>(undefined);
	const unregisterInlineRovoEditCancellation = useCallback(() => {
		unregisterInlineRovoEditCancellationRef.current?.();
		unregisterInlineRovoEditCancellationRef.current = undefined;
	}, []);

	useEffect(() => {
		setCancelRovoPrompt?.(undefined);
		return () => {
			setIsRovoLoading(false);
			unregisterInlineRovoEditCancellation();
		};
	}, [setCancelRovoPrompt, setIsRovoLoading, unregisterInlineRovoEditCancellation]);

	const teardownInlineSession = useCallback(
		({ streamEnded }: { streamEnded: boolean }) => {
			unregisterInlineRovoEditCancellation();
			cancel({ shouldClearSelectionContext: streamEnded });
			setIsLoadingBarMinimized(false);
			setIsRovoLoading(false);
			if (
				// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-phase-2 + cc-maui-experiment); will simplify post-rollout
				editorView &&
				fg('cc-maui-phase-2') &&
				expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true)
			) {
				createEndInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
			}
			api?.core?.actions?.execute(resetSessionState());
		},
		[
			cancel,
			setIsLoadingBarMinimized,
			setIsRovoLoading,
			api?.core?.actions,
			editorView,
			unregisterInlineRovoEditCancellation,
		],
	);

	const handleAbort = useCallback(
		({ streamEnded }: { streamEnded: boolean }) => {
			const attemptId = inlineRovoEditAttemptIdRef.current;
			const handedOff = admittedInlineRovoEditHandedOffRef.current;
			const pendingAutoClose = admittedInlineRovoEditPendingAutoCloseRef.current;
			const isSubmitAutoClose = submitAutoCloseRef.current;
			submitAutoCloseRef.current = false;
			if (handedOff) {
				return;
			}
			if (pendingAutoClose) {
				admittedInlineRovoEditPendingAutoCloseRef.current = false;
				// Rovo Chat now owns terminal settlement.
				cancel({ shouldClearSelectionContext: false });
				return;
			}
			if (attemptId) {
				api?.aiExperience?.actions.reportInlineRovoEdit?.(attemptId, 'generation-aborted');
			}
			teardownInlineSession({ streamEnded });
			if (isMauiOrMediaSelection && !isSubmitAutoClose) {
				remixEditExistingExperience.abort({
					metadata: { reason: 'user_cancel' },
				});
				void getBrowserBridgeAPI()?.send(AifcExperienceTrackingCommand, {
					status: 'abort',
					reason: 'user_cancel',
					experienceName: ExperienceName.REMIX_REGENERATE,
					senderId: ROVO_EXPERIENCE_TRACKING_SENDER_CLIENT_ID,
					receiverId: ROVO_CHAT_CLIENT_ID,
				});
			}
		},
		[api, cancel, teardownInlineSession, isMauiOrMediaSelection],
	);

	/**
	 * Error handoff (openChatOnError): same UI teardown as abort, but do not abort
	 * remix experiences. aiMateRemixRegenerate fails via send-message collect;
	 * remixEditExistingExperience is failed here because this path previously only aborted it.
	 */
	const handleError = useCallback(
		({ streamEnded }: { streamEnded: boolean }) => {
			const attemptId = inlineRovoEditAttemptIdRef.current;
			if (attemptId) {
				api?.aiExperience?.actions.reportInlineRovoEdit?.(attemptId, 'generation-error');
			}
			teardownInlineSession({ streamEnded });
			if (isMauiOrMediaSelection) {
				remixEditExistingExperience.failure({
					metadata: { reason: 'stream_error_open_chat' },
				});
			}
		},
		[api, teardownInlineSession, isMauiOrMediaSelection],
	);

	const handleBeforeSubmit = useCallback(
		(prompt: DocNode) => {
			submitAutoCloseRef.current = false;
			admittedInlineRovoEditHandedOffRef.current = false;
			admittedInlineRovoEditPendingAutoCloseRef.current = false;
			inlineRovoEditAttemptIdRef.current = undefined;
			const isMediaSelection = selectionContext?.selectionFragment?.some(
				(node: { type?: string }) => node.type === 'mediaSingle' || node.type === 'media',
			);
			const stagedTargetId =
				pendingInlineRovoStreamingTargetId ?? selectionContext?.localIds?.[0] ?? undefined;
			if (isMauiOrMediaSelection) {
				const beginInlineRovoEdit = api?.aiExperience?.actions.beginInlineRovoEdit;
				let admission: InlineRovoEditAdmissionResult = { status: 'unavailable' };
				if (beginInlineRovoEdit) {
					try {
						admission = beginInlineRovoEdit(stagedTargetId);
					} catch {
						// A broken optional lifecycle boundary must preserve the existing submit path.
						admission = { status: 'unavailable' };
					}
				}
				const producerAttempt: InlineRovoEditProducerAttemptObservation = {
					admissionHook:
						beginInlineRovoEdit && admission.status !== 'unavailable' ? 'available' : 'unavailable',
					artifactType: isMediaSelection ? 'image' : 'maui',
					outcome: admission.status,
					target: stagedTargetId ? 'present' : 'missing',
				};
				try {
					api?.aiExperience?.actions.reportInlineRovoEditProducerAttempt?.(producerAttempt);
				} catch {
					// Observability must never affect submission behavior.
				}
				inlineRovoEditAttemptIdRef.current =
					admission?.status === 'admitted' ? admission.attemptId : undefined;
				if (admission?.status === 'rejected') {
					// Reject before downstream request side effects.
					// The live target belongs to the edit already in progress, not this rejected submit.
					if (!isExperimentEnabled('cc_maui_1293_chat_thinking_bar')) {
						api?.aiExperience?.actions.endInlineRovoStreaming?.();
					}
					cancel({ shouldClearSelectionContext: false });
					return true;
				}
			}
			const onBeforeSubmitForRovoChat = openInRovoChatConfig?.onBeforeSubmit;
			if (onBeforeSubmitForRovoChat) {
				// Claim the synchronous prompt close before invoking the handoff.
				admittedInlineRovoEditHandedOffRef.current = Boolean(inlineRovoEditAttemptIdRef.current);
				try {
					const handled = onBeforeSubmitForRovoChat(prompt);
					if (handled) {
						startInlineRovoEditHandoffPresentation();
						return true;
					}
				} catch (error) {
					admittedInlineRovoEditHandedOffRef.current = false;
					const attemptId = inlineRovoEditAttemptIdRef.current;
					if (attemptId) {
						api?.aiExperience?.actions.reportInlineRovoEdit?.(attemptId, 'generation-aborted');
					}
					throw error;
				}
				admittedInlineRovoEditHandedOffRef.current = false;
			}

			if (isMauiOrMediaSelection) {
				// remixType is 'infographic' for media nodes; for MAUI apps it's
				// only known from the backend response (added via updateAttributes
				// in useEditorRovoBridgeCommandListener when the stream settles).
				const featureArea = getRemixFeatureArea({
					remixType: isMediaSelection ? 'infographic' : undefined,
				});
				void getBrowserBridgeAPI()?.send(AifcExperienceTrackingCommand, {
					status: 'start',
					experienceName: ExperienceName.REMIX_REGENERATE,
					actionId: selectionContext?.localIds?.[0],
					attributes: {
						regenerateSource: 'inline-existing-node',
						editSource: 'rovoBar',
						selectionLocalId: selectionContext?.localIds?.[0],
						...(featureArea ? { featureArea } : {}),
					},
					...(isMediaSelection && { remixType: 'infographic' }),
					senderId: ROVO_EXPERIENCE_TRACKING_SENDER_CLIENT_ID,
					receiverId: ROVO_CHAT_CLIENT_ID,
				});
			}

			const pendingAutoCloseAttemptId = inlineRovoEditAttemptIdRef.current;
			admittedInlineRovoEditPendingAutoCloseRef.current = Boolean(pendingAutoCloseAttemptId);
			// Expire the synchronous auto-close suppression if neither callback consumes it.
			queueMicrotask(() => {
				if (inlineRovoEditAttemptIdRef.current === pendingAutoCloseAttemptId) {
					admittedInlineRovoEditPendingAutoCloseRef.current = false;
				}
			});
			return false;
		},
		[
			api?.aiExperience?.actions,
			cancel,
			isMauiOrMediaSelection,
			openInRovoChatConfig,
			pendingInlineRovoStreamingTargetId,
			selectionContext,
			startInlineRovoEditHandoffPresentation,
		],
	);

	const onSubmit = useCallback(
		(cancel: () => void, abortTurn?: () => void) => {
			submitAutoCloseRef.current = false;
			admittedInlineRovoEditPendingAutoCloseRef.current = false;
			// Let the product cancel this attempt (e.g. its target node was deleted) through the
			// prompt's own Stop path. Prefer the hard abort: `cancel` shows a warning while hidden.
			const admittedAttemptId = inlineRovoEditAttemptIdRef.current;
			unregisterInlineRovoEditCancellation();
			if (admittedAttemptId) {
				unregisterInlineRovoEditCancellationRef.current =
					api?.aiExperience?.actions.registerInlineRovoEditCancellation?.(
						admittedAttemptId,
						abortTurn ?? cancel,
					);
			}
			// Promote the staged target id (set by the toolbar handler) — this is
			// what triggers the consumer-visible loading overlay. No-op when
			// nothing is staged (e.g. modal opened via a non-toolbar path).
			// `expValEqualsNoExposure` because the toolbar click is the designated
			// `cc-maui-experiment` exposure point.
			if (
				// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-phase-2 + cc-maui-experiment); will simplify post-rollout
				editorView &&
				fg('cc-maui-phase-2') &&
				expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true)
			) {
				createCommitInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
			}

			// Start remixRegenerate when editing an embedded MAUI/media node
			if (isMauiOrMediaSelection) {
				remixEditExistingExperience.start();
				remixEditExistingExperience.mark(REMIX_EDIT_EXISTING_MARKS.SUBMIT);
			}

			setIsLoadingBarMinimized(false);
			setIsRovoLoading(true);
			editorPluginAIProvider.onSubmit?.();
			// Submitting is what puts the freegen thinking bar on screen, so this is the
			// canonical exposure point for the collapse experiment.
			isCollapseLoadingBarEnabled();
			// Fire prompt event for AIFC. We don't have access to the request id as it's not generated
			// at this point of time and nothing else is available at this stage.
			// For now this will just give us an indication of prompt volume.
			api?.analytics?.actions.fireAnalyticsEvent<UserFlowAEP, 'customEventType'>({
				action: 'prompt',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'userFlow',
				attributes: {
					aiGroupId: nextSessionId,
					experienceName: CONFIG_ITEM_KEYS.FREE_GENERATE_AIFC,
					actionTaken: '',
					editCount: 0,
					aiSessionId: '',
					inlineStreamingAvailable: true,
				},
				eventType: EVENT_TYPE.UI,
			});

			const cancelRovoPrompt = () => {
				cancel();
			};
			// give DOM time to update the screen
			setTimeout(() => {
				setCancelRovoPrompt?.(() => cancelRovoPrompt);
			}, 100);
		},
		[
			api,
			nextSessionId,
			editorPluginAIProvider,
			setCancelRovoPrompt,
			setIsLoadingBarMinimized,
			setIsRovoLoading,
			editorView,
			isMauiOrMediaSelection,
			unregisterInlineRovoEditCancellation,
		],
	);

	const shouldShowCompact = useMemo(() => {
		if (!width) {
			return false;
		}

		return width <= Breakpoint.Small * 2;
	}, [width]);

	const firePromptInputEngaged = useRef(
		debounce((payload: PromptContentChangePayload, fireEvent: typeof api) => {
			fireEvent?.analytics?.actions.fireAnalyticsEvent<UserFlowAEP, 'customEventType'>({
				action: 'promptInputUpdated',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'userFlow',
				attributes: {
					aiGroupId: nextSessionId,
					experienceName: CONFIG_ITEM_KEYS.FREE_GENERATE_AIFC,
					actionTaken: '',
					editCount: 0,
					aiSessionId: '',
					inlineStreamingAvailable: true,
					characterCount: payload.textLength,
					hasMentionOrInlineCard: payload.hasMentionOrInlineCard,
				},
				eventType: EVENT_TYPE.UI,
			});
		}, 500),
	).current;

	useEffect(() => {
		return () => {
			firePromptInputEngaged.cancel();
		};
	}, [firePromptInputEngaged]);

	const isSmartlinkToolbarInlinePromptAnalyticsEnabled = expValEqualsNoExposure(
		'platform_editor_rovobutton_smartlink_toolbar_exp',
		'isEnabled',
		true,
	);

	const onPromptContentChange = useCallback(
		(payload: PromptContentChangePayload) => {
			if (payload.isEmpty && !payload.hasMentionOrInlineCard) {
				return;
			}
			if (isSmartlinkToolbarInlinePromptAnalyticsEnabled) {
				firePromptInputEngaged(payload, api);
			}
		},
		[api, firePromptInputEngaged, isSmartlinkToolbarInlinePromptAnalyticsEnabled],
	);

	// Memoized key forces RovoInlineChatPrompt to remount when the skill selection changes
	const userADFInputKey = useMemo(
		() => (userADFInput ? JSON.stringify(userADFInput) : undefined),
		[userADFInput],
	);

	// If a session is active - we'll show the loading screen elsewhere and we can hide this
	if (nextSessionId) {
		return null;
	}

	return (
		<FlagsProvider>
			<RovoInlineChatPrompt
				key={userADFInputKey}
				source={source}
				productKey={productKey}
				siteId={cloudId}
				orgId={orgId}
				objectId={targetId}
				_showLabsLozengeVariant={selectionLabsLozengeVariant}
				showMauiMediaPlaceholder={isMauiOrMediaSelection}
				hideAfterComplete={shouldHidePromptAfterComplete}
				openChatOnError={shouldOpenChatOnError}
				creationContextParams={editorPluginAIProvider.creationContextParams}
				onAbort={handleAbort}
				onError={handleError}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onCancel={() => {
					/* noop - this is to allow the rovo inline chat to manage cancel behaviour.
					 * We shouldn't end the AI experience directly. */
				}}
				popupsMountPoint={popupsMountPoint}
				// The following nops usages are like this because we want to ensure that only the AI plugin or the Orchestrator
				// are controlling the xstate. We do not want this to end the ai experience directly. Instead the rovo bridge
				// will call the orchestrator which will then end the experience, on both complete and abort.
				// IMPORTANT!! Do not end the AI experience on the complete callback. This can cause problems because the
				// rovo bridge may still have events to stream in the queue, and it's possible that this could result in
				// the experience being ended immediately on mount, which would then unmount the prompt. This is because the rovo bridge
				// can have a final response in its queue which has never fluushed previously, because something else unmounted this
				// component before the final response was flushed.
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onComplete={() => {
					if (!preserveLoadingBarMinimizedOnComplete || !isLoadingBarCollapseEnabled) {
						setIsLoadingBarMinimized(false);
					}
					setIsRovoLoading(false);
					if (
						// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-phase-2 + cc-maui-experiment); will simplify post-rollout
						editorView &&
						fg('cc-maui-phase-2') &&
						expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true)
					) {
						const inlineEditReadyTargetId =
							aiExperienceDecorationPluginKey.getState(editorView.state)
								?.inlineRovoStreamingTargetId ?? undefined;
						createEndInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
						// Keep the ready state separate from streaming completion, but carry the
						// edited node id across teardown so visibility and View target the result.
						if (isMauiOrMediaSelection) {
							createShowInlineEditReadyCommand(inlineEditReadyTargetId)(
								editorView.state,
								editorView.dispatch,
							);
						}
					}
				}}
				// When the progress bar killswitch is enabled, the inline chat's
				// onBeforeComplete will delay calling complete() until the progress bar
				// fill-to-100% animation finishes, preventing the UI from flashing the
				// prompt input before the parent unmounts this component.
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onBeforeComplete={
					isMauiOrMediaSelection
						? (complete) => {
								complete();
							}
						: undefined
				}
				onSubmit={onSubmit}
				onBeforeSubmit={handleBeforeSubmit}
				onSubmitAutoClose={handleSubmitAutoClose}
				getInlineRovoEditAttemptId={getInlineRovoEditAttemptId}
				enableAgents={editorPluginAIProvider.isRovoEnabled}
				currentUserId={
					(expValEquals('platform_editor_agent_mentions', 'isEnabled', true) &&
						fg('platform_editor_agent_mentions_drop_one_fixes')) ||
					fg('platform_editor_agent_mention_ask_rovo_jira')
						? userId
						: undefined
				}
				_showLabsLozenge={showLabsLozenge}
				compact={shouldShowCompact}
				onPromptContentChange={
					isSmartlinkToolbarInlinePromptAnalyticsEnabled ? onPromptContentChange : undefined
				}
				defaultValue={userADFInput}
				thinkingBarScrollContainer={thinkingBarScrollContainer}
				onThinkingBarDisplayedMinimizedChange={
					isLoadingBarCollapseEnabled ? setIsLoadingBarMinimized : undefined
				}
				thinkingBarWrapper={thinkingBarWrapper}
				useEditingWidthThinkingBar={useEditingWidthThinkingBar}
				shouldRenderInPortal={editorPluginAIProvider.shouldRenderInPortal}
				placeholderText={aifcPlaceholderText}
				agentSelectorLayout={agentSelectorLayout}
				hideAgentName={hideAgentName}
			/>
		</FlagsProvider>
	);
}
